import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";

const DEFAULT_COLOR = "#64748b";
const BLANK = { name: "", color: DEFAULT_COLOR, description: "" };

export function PostTypesManager() {
  const [types, setTypes] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, name, color, description}
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/post-types"); setTypes(data); }
    catch { setTypes([]); }
  };
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => [...(types || [])], [types]);

  const openNew = () => setEditing({ ...BLANK });
  const openEdit = (t) => setEditing({ id: t.id, name: t.name, color: t.color || DEFAULT_COLOR, description: t.description || "" });

  const save = async () => {
    const name = (editing.name || "").trim();
    if (!name) return toast.error("Name is required.");
    setSaving(true);
    const body = { name, color: editing.color || DEFAULT_COLOR, description: editing.description || "" };
    try {
      if (editing.id) await api.patch(`/admin/post-types/${editing.id}`, body);
      else await api.post("/admin/post-types", body);
      toast.success(editing.id ? "Post type updated" : "Post type created");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save post type."); }
    finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    try { await api.patch(`/admin/post-types/${t.id}`, { is_active: !t.is_active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doDelete = async () => {
    try { await api.delete(`/admin/post-types/${del.id}`); toast.success("Post type deactivated"); setDel(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!types) return <Loading />;

  return (
    <Card className="border-0 p-5" data-testid="post-types-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-[640] text-foreground">Post Types</h2>
          <p className="text-xs text-muted-foreground">Labels members apply to Chatter posts (up to 3 per post). Deactivated types stay on existing posts but are hidden from the composer.</p>
        </div>
        <Button onClick={openNew} data-testid="post-type-add"><Plus className="mr-1.5 h-4 w-4" /> Add post type</Button>
      </div>

      <div className="mt-4 divide-y divide-border/70">
        {sorted.map((t) => (
          <div key={t.id} className={`flex items-center gap-3 py-2.5 ${t.is_active ? "" : "opacity-50"}`} data-testid={`post-type-row-${t.slug}`}>
            <span className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border border-border" style={{ backgroundColor: t.color }} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-[560] text-foreground">{t.name}{t.system ? <span className="ml-1.5 text-[11px] font-[500] text-muted-foreground">· system</span> : null}</div>
              <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="font-mono">{t.slug}</span>
                <span data-testid={`post-type-usage-${t.slug}`}>· {t.usage_count} {t.usage_count === 1 ? "post" : "posts"}</span>
              </div>
            </div>
            <Switch checked={!!t.is_active} onCheckedChange={() => toggleActive(t)} data-testid={`post-type-active-${t.slug}`} />
            <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`post-type-edit-${t.slug}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDel(t)} data-testid={`post-type-delete-${t.slug}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {sorted.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No post types yet.</p>}
      </div>

      {/* Add / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="post-type-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit post type" : "New post type"}</DialogTitle>
            <DialogDescription>Post types are labels members can apply to their Chatter posts (up to 3 per post).</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} data-testid="post-type-name" placeholder="e.g. Announcement" />
              </div>
              <div className="space-y-1.5">
                <Label>Color</Label>
                <div className="flex items-center gap-3">
                  <input type="color" value={editing.color || DEFAULT_COLOR} onChange={(e) => setEditing((s) => ({ ...s, color: e.target.value }))} className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent p-0.5" data-testid="post-type-color" />
                  <Input value={editing.color || ""} onChange={(e) => setEditing((s) => ({ ...s, color: e.target.value }))} className="w-32 font-mono" />
                  <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-[560]" style={{ borderColor: editing.color || DEFAULT_COLOR, color: editing.color || DEFAULT_COLOR }}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: editing.color || DEFAULT_COLOR }} />
                    {editing.name || "Preview"}
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Description <span className="text-muted-foreground">(internal)</span></Label>
                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))} data-testid="post-type-description" placeholder="What is this post type for?" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="post-type-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Soft-delete confirm */}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate “{del?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>The post type is soft-deleted: it stays on posts that already use it and is hidden from the composer. You can reactivate it anytime with the toggle.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete} data-testid="post-type-delete-confirm">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default PostTypesManager;
